import { useAuth0 } from '@auth0/auth0-react'
import { useLogin } from '../../auth/useLogin'
import { useI18n, useT } from '../../i18n/useT'
import { formatRenewalDate } from '../../lib/creditRenewal'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'

interface LimitReachedModalProps {
  isOpen: boolean
  onClose: () => void
  isSubscribed?: boolean
  /** When the credits come back — null when there is nothing honest to
   * promise (see lib/creditRenewal.ts) */
  renewsOn?: Date | null
  /** The zero is the per-IP signup brake's doing, not the user's spending */
  bonusDeclined?: boolean
}

/** The wall. Three different people hit it, and only one of them should be
 * sold a plan:
 *
 *  - a visitor with no account, whose next step is free (register) and used
 *    to be quoted a price;
 *  - a free account, whose credits come back on the 1st — saying so is the
 *    whole point of a monthly tier, otherwise it reads like the old one-off
 *    bonus and the wall looks permanent;
 *  - a subscriber, whose credits also come back, on the billing date. Left
 *    with the upgrade pitch alone, the paying customer would be the only one
 *    not told their balance returns.
 */
export function LimitReachedModal({
  isOpen,
  onClose,
  isSubscribed = false,
  renewsOn = null,
  bonusDeclined = false,
}: LimitReachedModalProps) {
  const { isAuthenticated } = useAuth0()
  const login = useLogin()
  const { locale } = useI18n()
  const t = useT()

  const date = renewsOn ? formatRenewalDate(renewsOn, locale) : ''

  function showPlans() {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
    onClose()
  }

  function register() {
    login({ screenHint: 'signup' })
    onClose()
  }

  function body(): string {
    if (!isAuthenticated) return t('limit.bodyAnon')
    if (bonusDeclined) return date ? t('limit.bodyDeclined', { date }) : t('limit.bodyDeclinedNoDate')
    if (isSubscribed) return date ? t('limit.bodyAuth', { date }) : t('limit.bodyAuthNoDate')
    return date ? t('limit.bodyFree', { date }) : t('limit.bodyFreeNoDate')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isAuthenticated ? t('limit.title') : t('limit.titleAnon')}>
      <div className="text-center py-2">
        <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-text-secondary text-sm mb-6">{body()}</p>
        <div className="flex gap-3">
          {isAuthenticated ? (
            <>
              <Button variant="secondary" onClick={onClose} className="flex-1">
                {t('common.cancel')}
              </Button>
              <Button onClick={showPlans} className="flex-1">
                {t('limit.viewPlans')}
              </Button>
            </>
          ) : (
            <>
              {/* Free step first — in reading order, not just in weight. The
                  visitor who just got a result and wants another one is one
                  click from a free account; leading with the paid option
                  undercuts the whole point of saying so. */}
              <Button onClick={register} className="flex-1">
                {t('limit.createAccount')}
              </Button>
              <Button variant="secondary" onClick={showPlans} className="flex-1">
                {t('limit.viewPlans')}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
